import classNames from "classnames";
import SimpleBar from "simplebar-react";
import Skeleton from "react-loading-skeleton";
import { Crown } from "frontend/icons/crown";
import { MondayLogo } from "frontend/icons/monday-logo";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { cleanAccountName } from "shared/util/utils";
import consts from "shared/consts";
import { CanvasTagId, CanvasTemplateTag } from "shared/datamodel/schemas/canvas-template";
import style from "../../templates-modal.module.css";
import { useRef, useState } from "react";

interface TemplateTagListProps {
  tags: CanvasTemplateTag[];
  selectedTag: { id: CanvasTagId; name: string };
  showUpgradeCTA?: boolean;
  syncedTemplatesSectionId: number;
  shouldShowSyncedOnTop: boolean;
  accountName: string;
  onTagClicked: (tagId: CanvasTagId, tagName: string) => void;
  onUpgradeCTAClick: (source: string) => void;
}

export function TemplateTagList({
  tags,
  selectedTag,
  showUpgradeCTA,
  syncedTemplatesSectionId,
  shouldShowSyncedOnTop,
  accountName,
  onTagClicked,
  onUpgradeCTAClick,
}: TemplateTagListProps) {
  const accountNameRef = useRef<any>(null);
  const [tooltip, setTooltip] = useState<any>(null);

  function tagStyle(id: CanvasTagId) {
    return classNames(style.tag, {
      [style.selected]: id === selectedTag.id,
    });
  }

  function renderCategoryTags() {
    if (tags.length === 0) {
      return <Skeleton count={12} className={style.tagsSkeleton} />;
    }

    return tags.map((tag: CanvasTemplateTag) => {
      if (shouldShowSyncedOnTop && tag.id === syncedTemplatesSectionId) {
        return null;
      }
      return (
        <div role="button" key={tag.id} className={tagStyle(tag.id!)} onClick={() => onTagClicked(tag.id!, tag.name)}>
          {tag.name}
        </div>
      );
    });
  }

  function renderSpecialTags() {
    const syncedTag = tags.find((tag) => tag.id === syncedTemplatesSectionId);
    return (
      <>
        <div
          className={tagStyle(consts.MY_TEMPLATES_TAG_ID)}
          onClick={() => onTagClicked(consts.MY_TEMPLATES_TAG_ID, "My Templates")}
          data-testid="my-templates-tag"
        >
          My Templates
        </div>
        <div
          className={tagStyle(consts.ACCOUNT_TEMPLATES_TAG_ID)}
          onClick={() => onTagClicked(consts.ACCOUNT_TEMPLATES_TAG_ID, `${accountName} Templates`)}
          data-testid="account-templates-tag"
        >
          {tooltip && (
            <Tooltip
              relativeTo={accountNameRef}
              label={accountNameRef.current.textContent}
              side={"bottom"}
              withArrow={false}
              customStyle={{
                position: "absolute",
                width: "fit-content",
                backgroundColor: "#EBEDF3",
                color: "#113357",
                padding: "0 12px",
                boxShadow: "0px 2px 4px 0px #00000026",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                font: "400 16px Poppins",
                whiteSpace: "nowrap",
                height: "36px",
                marginLeft: 176,
                marginTop: -10,
              }}
            />
          )}
          <span
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              display: "block",
            }}
            onMouseEnter={() => {
              const isTextOverflowing =
                accountNameRef.current && accountNameRef.current.scrollWidth > accountNameRef.current.clientWidth;
              isTextOverflowing ? setTooltip(accountNameRef.current.innerText) : setTooltip(null);
            }}
            onMouseLeave={() => setTooltip(null)}
            ref={accountNameRef}
          >
            {`${cleanAccountName(accountName)} Templates`}
          </span>
        </div>
        {shouldShowSyncedOnTop && syncedTag && (
          <div
            className={tagStyle(syncedTemplatesSectionId)}
            onClick={() => onTagClicked(syncedTemplatesSectionId, syncedTag.name)}
          >
            <MondayLogo size={16} />
            {syncedTag.name}
          </div>
        )}
      </>
    );
  }

  return (
    <div className={style.tagsContainer}>
      <SimpleBar
        scrollbarMaxSize={100}
        style={{
          maxHeight: "100%",
        }}
      >
        <div className={style.tags}>
          {showUpgradeCTA && (
            <div className={style.unlockButton} onClick={() => onUpgradeCTAClick("Unlock all templates")}>
              <Crown size={14} />
              <span>Unlock all templates</span>
            </div>
          )}
          <div
            className={tagStyle(consts.ALL_TEMPLATES_TAG_ID)}
            onClick={() => onTagClicked(consts.ALL_TEMPLATES_TAG_ID, "All Templates")}
          >
            All Templates
          </div>
          {renderSpecialTags()}
          <div className={style.separator} />
          {renderCategoryTags()}
        </div>
      </SimpleBar>
    </div>
  );
}
